import { getAPYForPID } from "./farmConfigs.js";
import BigNumber from "bignumber.js";
const { rpcUrl, chainId } = require("./configs");
const Web3 = require("web3");
const WEEK_SECONDS = 604800;

let web3 = new Web3(rpcUrl);
const { BN } = web3.utils;

export const getRealProvider = async (ethereum) => {
  let realProvider;

  if (typeof ethereum === "string") {
    if (ethereum.includes("wss")) {
      realProvider = new Web3.providers.WebsocketProvider(ethereum, {
        timeout: 10000,
      });
    } else {
      realProvider = new Web3.providers.HttpProvider(ethereum, {
        timeout: 10000,
      });
    }
  } else {
    realProvider = ethereum;
  }

  return new Web3(realProvider);
};

export const getRealContract = async (address, ethereum, abi) => {
  const web3 = await getRealProvider(ethereum);
  return new web3.eth.Contract(abi, address);
};

export const getRewardToken = async (farmContract) => {
  return await farmContract.methods.corsacERC20();
};

export const getCurrentBlock = async (web3Arg) => {
  return web3Arg
    ? await web3Arg.eth.getBlockNumber()
    : await web3.eth.getBlockNumber();
};

/**
 *
 * @returns {Promise<number>} average time is seconds
 */
export const getAverageBlockTime = async (web3Args) => {
  // we average the future bloc time based on the average bloc time in the last one million block
  web3Args = web3Args ? web3Args : web3;
  const currentBlock = await getCurrentBlock(web3Args);
  const pastBlockTime = (await web3Args.eth.getBlock(currentBlock - 1000000))
    .timestamp;
  const currentBlockTime = Date.now() / 1000;
  return (currentBlockTime - pastBlockTime) / 1000000;
};

/**
 *
 * @param farmContract
 * @param ethereum
 * @param rewardsPerBlock
 * @param totalAllocationPoints
 * @param priceProvider
 * @param corsacPriceInUSD
 * @param pricesProvider
 * @param farmConfig
 * @returns {Promise<*[]>}
 */
export const getFarms = async (
  farmContract,
  ethereum,
  { rewardsPerBlock, totalAllocationPoints },
  priceProvider,
  corsacPriceInUSD,
  farmConfig
) => {
  const pools = [...farmConfig.farms];

  for (let i = 0; i < farmConfig.farms.length; i++) {
    pools[i] = await getFarmDetails({
      farm: pools[i],
      rewardsPerBlock,
      totalAllocPoints: totalAllocationPoints,
      farmContract,
      ethereum,
      priceProvider,
      corsacPriceInUSD,
    });
  }
  return pools;
};
/**
 *
 * @param farm
 * @param rewardsPerBlock
 * @param totalAllocPoints
 * @param farmContract
 * @param ethereum
 * @param priceProvider
 * @param corsacPrice
 * @returns {Promise<{
 * lpToken: string,
 * allocationPoints: *,
 * lastRewardBlock: *,
 * apy: (number|*),
 * accERC20PerShare: *,
 * totalRewardsPerWeek: number,
 * totalStaked: string
 *  TODO : add the rest of the Fields
 * }>}
 */
export const getFarmDetails = async ({
  farm,
  rewardsPerBlock,
  totalAllocPoints,
  farmContract,
  ethereum,
  priceProvider,
  corsacPriceInUSD,
}) => {
  let pool = await farmContract.methods.poolInfo(farm.pid).call();
  const poolDistPerBlock =
    (Number(pool.allocPoint) / Number(totalAllocPoints)) * Number(rewardsPerBlock);

  const blocksPerWeek = WEEK_SECONDS / 3;
  const totalRewardsPerWeek = blocksPerWeek * poolDistPerBlock;

  const active = await isActive(farmContract);
  const apy = active
    ? await getAPYForPID(
        farm.pid,
        pool,
        totalRewardsPerWeek,
        priceProvider,
        corsacPriceInUSD,
        farm.stakedToken.decimals
      )
    : new BigNumber("0");

  // lp apy
  const startBlock = await farmContract.methods.startBlock().call();
  const currentBlock = await getCurrentBlock();
  const lpRewardsPerBlock = pool.totalLPRewards / (currentBlock - startBlock);
  const lpRewardsPerWeek = blocksPerWeek * lpRewardsPerBlock;

  const lpPrices = await priceProvider.getLPPricesPrice();
  const apy2 = active
    ? await getAPYForPID(
        farm.pid,
        pool,
        lpRewardsPerWeek,
        priceProvider,
        lpPrices[farm.pid],
        farm.stakedToken.decimals,
        farm.stakedToken.decimals
      )
    : new BigNumber("0");

  return {
    ...farm,
    // some legacy functions depend on this we remove when we clear them out
    lpToken: pool.lpToken,
    allocationPoints: pool.allocPoint,
    lastRewardBlock: pool.lastRewardBlock,
    accERC20PerShare: pool.accERC20PerShare,
    totalStaked: pool.stakedAmount,
    lockPeriod: pool.lockPeriod,
    penalty: pool.penalty,
    penaltyStakers: pool.penaltyStakers,
    accLPPerShare: pool.accLPPerShare,
    totalLPRewards: pool.totalLPRewards,
    lpRewardsClaimed: pool.lpRewardsClaimed,
    tvl:
      (Number(pool.stakedAmount) / 10 ** farm.stakedToken.decimals) *
      (await priceProvider.getLPPricesPrice())[farm.pid],
    totalRewardsPerWeek,
    apy: apy,
    apyLp: apy2,
  };
};

export const getUserDeposits = async (farmContract, pid, account) => {
  const deposits = await farmContract.methods.getUserDeposits(pid, account).call();
  return deposits;
};

/**
 *
 * @param farmContract
 * @param pid
 * @param account
 * @returns {Promise<*>}
 */
export const getStaked = async (farmContract, pid, account) => {
  return farmContract.methods.totalDeposited(pid, account).call();
};

/**
 *
 * @param farmContract
 * @param pid
 * @param account
 * @returns {Promise<BN|*>}
 */
export const getPending = async (farmContract, pid, account) => {
  // TODO revert this workaround
  try {
    const pending = await farmContract.methods.pending(pid, account).call();
    return pending;
  } catch (error) {
    await sleep(500);
    const userInfo = await farmContract.methods.userInfo(pid, account).call();

    const poolInfo = await farmContract.methods.poolInfo(pid).call();

    const accERC20PerShare = poolInfo.accERC20PerShare;

    return new BN(userInfo.amount)
      .mul(new BN(accERC20PerShare))
      .div(new BN("1000000000000000000000000000000000000"))
      .sub(new BN(userInfo.rewardDebt));
  }
};

/**
 *
 * @param farmContract
 * @param pid
 * @param account
 * @returns {Promise<BN|*>}
 */
export const getPendingLP = async (farmContract, pid, account) => {
  const pending = await farmContract.methods.pendingInLPRewards(pid, account).call();
  return pending;
};

/**
 *
 * @param farmContract
 * @returns {Promise<*>}
 */
export const getTotalAllocPoints = async (farmContract) => {
  return farmContract.methods.totalAllocPoint().call();
};
/**
 *
 * @param farmContract
 * @returns {Promise<*>}
 */
export const getRewardsPerBlock = async (farmContract) => {
  return farmContract.methods.rewardPerBlock().call();
};
/**
 *
 * @param farmContract
 * @returns {Promise<*>}
 */
export const isPaused = async (farmContract) => {
  return farmContract.methods.paused().call();
};

/**
 *
 * @param farmContract
 * @returns {Promise<*>}
 */
export const isInEmergency = async (farmContract) => {
  return farmContract.methods.inEmergency().call();
};

/**
 *
 * @param farmContract
 * @returns {Promise<boolean>}
 */
export const isActive = async (farmContract) => {
  return (await farmContract.methods.endBlock().call()) > (await getCurrentBlock());
};
/**
 *
 * @param farmContract
 * @returns {Promise<boolean>}
 */
export const hasFarmStarted = async (farmContract) => {
  return (await farmContract.methods.endBlock().call()) != 0;
};
// Write
/**
 *
 * @param farmContract
 * @param pid
 * @param wallet
 * @returns {Promise<*>}
 */
export const harvest = async (farmContract, pid, wallet) => {
  const data = farmContract.methods.stakeInPool(pid, "0").encodeABI();

  const transactionParams = {
    to: farmContract.options.address,
    from: wallet.account,
    data: data,
    chainId: chainId,
  };

  return wallet.ethereum.request({
    method: "eth_sendTransaction",
    params: [transactionParams],
  });
};
/**
 *
 * @param farmContract
 * @param pid
 * @param depositId
 * @param wallet
 * @returns {Promise<*>}
 */
export const withdrawUnlocked = async (farmContract, pid, depositId, wallet) => {
  const data = farmContract.methods
    .withdrawUnlockedDeposit(pid, depositId)
    .encodeABI();

  const transactionParams = {
    to: farmContract.options.address,
    from: wallet.account,
    data: data,
    chainId: chainId,
  };

  return wallet.ethereum.request({
    method: "eth_sendTransaction",
    params: [transactionParams],
  });
};

/**
 *
 * @param farmContract
 * @param pid
 * @param depositId
 * @param wallet
 * @returns {Promise<*>}
 */
export const withdrawLocked = async (farmContract, pid, depositId, wallet) => {
  const data = farmContract.methods.unstakeWithPenalty(pid, depositId).encodeABI();

  const transactionParams = {
    to: farmContract.options.address,
    from: wallet.account,
    data: data,
    chainId: chainId,
  };

  return wallet.ethereum.request({
    method: "eth_sendTransaction",
    params: [transactionParams],
  });
};

/**
 *
 * @param farmContract
 * @param pid
 * @param wallet
 * @returns {Promise<*>}
 */
export const emergencyWithdrawFromFarm = async (farmContract, pid, wallet) => {
  const data = farmContract.methods.emergencyWithdraw(pid).encodeABI();

  const transactionParams = {
    to: farmContract.options.address,
    from: wallet.account,
    data: data,
    chainId: chainId,
  };

  return wallet.ethereum.request({
    method: "eth_sendTransaction",
    params: [transactionParams],
  });
};
/**
 *
 * @param farmContract
 * @param pid
 * @param amount
 * @param wallet
 * @returns {Promise<*>}
 */
export const stake = async (farmContract, pid, amount, wallet) => {
  const data = farmContract.methods.stakeInPool(pid, amount).encodeABI();

  const transactionParams = {
    to: farmContract.options.address,
    from: wallet.account,
    data: data,
    chainId: chainId,
  };

  return wallet.ethereum.request({
    method: "eth_sendTransaction",
    params: [transactionParams],
  });
};
/**
 *
 * @param pendingTxHash
 * @returns {Promise<unknown>}
 */
// helper functions
export const waitForTransaction = async (pendingTxHash) => {
  return new Promise(async (resolve, reject) => {
    let receipt;
    do {
      await sleep(2200); // this will be roughly one block on Polygon main net
      receipt = await web3.eth.getTransactionReceipt(pendingTxHash);
    } while (!receipt);
    await sleep(4000); // lets wait for 2 more blocks to comfirm
    resolve(receipt);
  });
};

/**
 *
 * @param ms
 * @returns {Promise<unknown>}
 */
export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
